import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class HealthCheckService {
  private http = inject(HttpClient);

  // Signal to track if the system is healthy
  isHealthy = signal<boolean>(true);
  private isChecking = false;
  private platformId = inject(PLATFORM_ID);

  constructor() {}

  /**
   * Called by interceptors when a critical API error (500/0) occurs.
   */
  onApiError() {
    if (!isPlatformBrowser(this.platformId) || this.isChecking) return;

    this.isChecking = true;
    this.checkHealth().subscribe((healthy) => {
      this.isHealthy.set(healthy);
      this.isChecking = false;
    });
  }

  /**
   * Manually reset to healthy (e.g. on page reload or manual refresh)
   */
  resetHealth() {
    this.isHealthy.set(true);
  }

  checkHealth() {
    // Typically health checks are at the root, not under /api
    const healthUrl = environment.apiUrl.endsWith('/api')
      ? environment.apiUrl.replace(/\/api$/, '/healthz')
      : `${environment.apiUrl}/healthz`;

    return this.http.get(healthUrl, { responseType: 'text' }).pipe(
      map(() => true),
      catchError((error) => {
        // If status is 0 (connection refused) or >= 500 (server error), we go to maintenance.
        // If it's 404, it means the server is UP, but /healthz isn't mapped, which still means the backend is alive.
        if (error.status === 0 || error.status >= 500) {
          return of(false);
        }
        return of(true);
      }),
    );
  }
}
